import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import StepUpDialog from "@/components/StepUpDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendingUp, ArrowDownToLine, Banknote, AlertTriangle } from "lucide-react";

const Stat = ({ label, value, hint, negative, testid }) => (
  <div className="rounded-2xl bg-white border p-4" data-testid={testid}>
    <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${negative ? "text-rose-600" : "text-slate-900"}`}>{negative && value ? "-" : ""}₹{Number(value || 0).toFixed(0)}</div>
    {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
  </div>
);

const PayoutMethodCard = ({ pm, onAdd }) => {
  if (!pm) return null;
  const has = pm.has_bank || pm.has_upi;
  return (
    <div className="rounded-2xl bg-white border p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="payout-method-card">
      <div className="text-sm">
        <div className="font-semibold">Payout method</div>
        <div className="text-slate-600 text-xs mt-0.5">
          {!has ? "No bank account or UPI ID added yet." : `${pm.has_bank ? "Bank account" : ""}${pm.has_bank && pm.has_upi ? " + " : ""}${pm.has_upi ? "UPI ID" : ""} on file · ${pm.verified ? "verified" : "awaiting verification before your first payout"}`}
        </div>
      </div>
      {!has && <Button size="sm" variant="outline" className="rounded-full" onClick={onAdd} data-testid="add-payout-method-btn">Add details</Button>}
    </div>
  );
};

const TransactionHistory = ({ rows }) => (
  <div className="rounded-2xl bg-white border p-5" data-testid="earnings-transactions">
    <div className="font-display font-bold mb-3">Transaction history</div>
    {rows.length === 0 && <div className="text-sm text-slate-500">No transactions yet.</div>}
    <div className="divide-y divide-slate-100">
      {rows.map((t, i) => (
        <div key={i} className="flex items-center justify-between text-sm py-2" data-testid={`txn-${i}`}>
          <div>
            <div className="font-medium">{t.label}</div>
            <div className="text-xs text-slate-500">{t.at ? new Date(t.at).toLocaleString() : ""}</div>
          </div>
          <div className={`font-mono ${t.amount >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{t.amount >= 0 ? "+" : "-"}₹{Math.abs(t.amount).toFixed(0)}</div>
        </div>
      ))}
    </div>
  </div>
);

const WD_STEPS = ["requested", "verified", "paid"];
const WithdrawalStatus = ({ rows }) => {
  if (!rows.length) return null;
  return (
    <div className="rounded-2xl bg-white border p-5" data-testid="withdrawal-status-list">
      <div className="font-display font-bold mb-3">Withdrawals</div>
      <div className="space-y-3">
        {rows.slice(0, 10).map((w) => {
          const st = w.status === "pending" ? "requested" : w.status;
          const idx = WD_STEPS.indexOf(st);
          return (
            <div key={w.id} className="text-sm" data-testid={`withdrawal-${w.id}`}>
              <div className="flex justify-between"><span className="font-semibold">₹{Number(w.amount).toFixed(0)}</span><span className="text-xs text-slate-500">{new Date(w.created_at).toLocaleDateString()}</span></div>
              <div className="flex items-center gap-1 mt-1.5" data-testid={`withdrawal-status-${w.id}`}>
                {WD_STEPS.map((s, i) => (
                  <React.Fragment key={s}>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${i <= idx ? "bg-emerald-100 text-emerald-800 font-semibold" : "bg-slate-100 text-slate-400"}`}>{s}</span>
                    {i < 2 && <span className="text-slate-300">→</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CarrierEarnings = ({ user, onRefresh }) => {
  const [w, setW] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [amount, setAmount] = useState("");
  const [upi, setUpi] = useState(user?.upi_id || "");
  const [acctName, setAcctName] = useState(user?.account_holder_name || "");
  const [acctNumber, setAcctNumber] = useState(user?.account_number || "");
  const [ifsc, setIfsc] = useState(user?.ifsc_code || "");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState(user?.accepts_perishables || false);
  const [wds, setWds] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [stepUp, setStepUp] = useState(false);

  const load = async () => {
    const w1 = await api.get("/wallet"); setW(w1.data);
    try { const e = await api.get("/users/me/earnings"); setEarnings(e.data); } catch {}
    try { const c = await api.get("/marketplace/config"); setCfg(c.data); } catch {}
    try { const m = await api.get("/withdrawals/mine"); setWds(m.data); } catch {}
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => setPrefs(user?.accepts_perishables || false), [user]);
  useEffect(() => {
    if (user) {
      setUpi(user.upi_id || "");
      setAcctName(user.account_holder_name || "");
      setAcctNumber(user.account_number || "");
      setIfsc(user.ifsc_code || "");
    }
  }, [user]);

  const minWithdrawal = Number(cfg?.min_withdrawal_rupees || 200);
  const walletAvailable = Number(earnings?.available_balance ?? w?.balance ?? 0);
  const bankSaved = !!(user?.account_holder_name && user?.account_number && user?.ifsc_code);
  const detailsChanged = acctName.trim() !== (user?.account_holder_name || "") || acctNumber.trim() !== (user?.account_number || "")
    || ifsc.trim().toUpperCase() !== (user?.ifsc_code || "") || upi.trim() !== (user?.upi_id || "");

  const doWithdraw = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < minWithdrawal) {
      return toast.error(`Minimum withdrawal is ₹${minWithdrawal.toFixed(0)}.`);
    }
    if (amt > walletAvailable) {
      return toast.error("Amount is more than your available earnings.");
    }
    const hasBank = acctName.trim() && acctNumber.trim() && ifsc.trim();
    if (!hasBank && !upi.trim()) {
      return toast.error("Please add your bank account or UPI ID to continue.");
    }
    if (detailsChanged) return setStepUp(true);
    setConfirming(true);
  };
  const submit = async (step_up_token) => {
    setBusy(true);
    try {
      await api.post("/withdrawals", {
        amount: parseFloat(amount),
        account_holder_name: acctName.trim() || null,
        account_number: acctNumber.trim() || null,
        ifsc_code: ifsc.trim().toUpperCase() || null,
        upi_id: upi.trim() || null,
        step_up_token,
      });
      toast.success("Withdrawal requested");
      setOpen(false); setAmount(""); setConfirming(false); setStepUp(false);
      load(); onRefresh?.();
    } catch (e) {
      toast.error(apiError(e, "Failed"));
    } finally { setBusy(false); }
  };

  const togglePerishables = async (v) => {
    setPrefs(v);
    await api.patch("/users/me", { accepts_perishables: v });
    onRefresh?.();
    toast.success(v ? "Now accepting perishables/liquids" : "Perishables/liquids off");
  };

  return (
    <div className="space-y-4">
      <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 shadow-lg">
        <div className="flex items-center gap-2 text-xs text-amber-300 uppercase tracking-widest"><TrendingUp className="w-3.5 h-3.5"/> Earnings · available</div>
        <div className="mt-2 font-display text-5xl font-extrabold" data-testid="earnings-available">₹{walletAvailable.toFixed(0)}</div>
        <div className="mt-1 text-sm text-slate-300">What CarryIt owes you for completed deliveries. It can only be withdrawn to your own bank account or UPI ID.</div>
        <Button data-testid="withdraw-btn" onClick={() => setOpen(true)} className="mt-4 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
          <ArrowDownToLine className="w-4 h-4 mr-1"/> Request withdrawal
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Stat testid="earnings-available-stat" label="Available balance" value={walletAvailable}/>
        <Stat testid="earnings-pending" label="Pending" value={earnings?.pending_total}
              hint={`₹${Number(earnings?.held_amount || 0).toFixed(0)} delivered · withdrawable 24h after delivery · ₹${Number(earnings?.in_progress_amount || 0).toFixed(0)} on ${earnings?.in_progress_count || 0} deliveries in progress`}/>
        <Stat testid="earnings-commission" label="Commission deducted" value={earnings?.commission_deducted} hint="COD commission taken from your earnings" negative/>
      </div>

      <PayoutMethodCard pm={earnings?.payout_method} onAdd={() => setOpen(true)}/>

      {/* Spec §5: COD running balance panel */}
      {earnings?.cod && (
        <div
          className={`rounded-2xl border p-5 ${
            earnings.cod.cod_balance_owed > 0
              ? "bg-amber-50 border-amber-200"
              : "bg-white border-slate-200"
          }`}
          data-testid="cod-balance-card"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs text-slate-600 uppercase tracking-wider flex items-center gap-1">
                <Banknote className="w-3 h-3"/> Cash on delivery balance
              </div>
              <div className="font-display text-3xl font-extrabold mt-1 text-slate-900" data-testid="cod-balance-amount">
                ₹{Number(earnings.cod.cod_balance_owed || 0).toFixed(0)}
              </div>
              <div className="text-xs text-slate-600 mt-1">
                {earnings.cod.cod_balance_owed > 0
                  ? <>Commission you owe CarryIt from past COD deliveries. We'll deduct it from your earnings on your next online delivery.</>
                  : <>No COD commission outstanding — new COD jobs are open to you.</>}
              </div>
            </div>
            {!earnings.cod.cod_eligible && (
              <div className="text-right max-w-[55%]" data-testid="cod-blocked">
                <div className="text-[10px] uppercase tracking-wider text-amber-800">COD paused</div>
                <div className="text-xs text-amber-900 mt-0.5">
                  {(earnings.cod.cod_block_reasons || [])[0] || "Carrier is not eligible for COD right now."}
                </div>
              </div>
            )}
          </div>
          {earnings.cod.cod_balance_owed > earnings.cod.cod_max_outstanding && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
              <div>
                You're above the ₹{Number(earnings.cod.cod_max_outstanding).toFixed(0)} COD limit.
                Please complete an online delivery to settle before any new cash jobs appear.
              </div>
            </div>
          )}
          {(earnings.cod.ledger || []).length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-slate-600 cursor-pointer hover:text-slate-900" data-testid="cod-ledger-toggle">
                Show recent COD activity ({earnings.cod.ledger.length})
              </summary>
              <div className="mt-2 space-y-1">
                {earnings.cod.ledger.slice(0, 20).map((row, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-none">
                    <div>
                      <span className="font-medium text-slate-700">
                        {row.kind === "debit_commission" ? "Commission booked" : "Settled from payout"}
                      </span>
                      <span className="text-slate-400"> · {new Date(row.at).toLocaleDateString()}</span>
                    </div>
                    <div className={`font-mono ${row.amount >= 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {row.amount >= 0 ? "+" : ""}₹{Number(row.amount).toFixed(0)}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      <div className="rounded-2xl bg-white border p-5 flex items-center justify-between">
        <div>
          <div className="font-semibold">Accept perishables & liquids</div>
          <div className="text-xs text-slate-500">Available for tier 1+ carriers only</div>
        </div>
        <Switch data-testid="perishable-toggle" checked={prefs} onCheckedChange={togglePerishables}/>
      </div>

      <WithdrawalStatus rows={wds}/>
      <TransactionHistory rows={earnings?.transactions || []}/>
      <StepUpDialog open={stepUp} purpose="payout" title="Confirm your payout details" onClose={() => setStepUp(false)} onVerified={submit}/>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle>Withdraw your earnings</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider">Amount (₹)</label>
              <Input
                data-testid="amount-input" type="number" value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder={`Minimum ₹${minWithdrawal.toFixed(0)}`}
                className="mt-1 rounded-xl"
              />
              <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
                <span>Available: ₹{walletAvailable.toFixed(0)}</span>
                <span>Minimum: ₹{minWithdrawal.toFixed(0)}</span>
              </div>
            </div>
            {!bankSaved && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                Add a bank account or a UPI ID below — we'll confirm it with a one-time code and verify it once before your first payout.
              </div>
            )}
            <div className="grid gap-3">
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider">Account holder name</label>
                <Input data-testid="acct-name-input" value={acctName} onChange={e => setAcctName(e.target.value)} className="mt-1 rounded-xl"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider">Account number</label>
                <Input data-testid="acct-num-input" value={acctNumber} onChange={e => setAcctNumber(e.target.value)} className="mt-1 rounded-xl font-mono"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider">IFSC code</label>
                <Input data-testid="ifsc-input" value={ifsc} onChange={e => setIfsc(e.target.value.toUpperCase())} placeholder="HDFC0000001" className="mt-1 rounded-xl font-mono uppercase"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider">UPI ID (instead of, or as well as, a bank account)</label>
                <Input data-testid="upi-input" value={upi} onChange={e => setUpi(e.target.value)} placeholder="you@upi" className="mt-1 rounded-xl font-mono"/>
              </div>
            </div>
          </div>
          {confirming && (
            <div className="rounded-xl bg-slate-50 border p-3 text-sm" data-testid="withdraw-confirm-panel">
              Withdraw <b>₹{Number(amount || 0).toFixed(0)}</b> to {acctNumber ? <>bank account <span className="font-mono">•••• {acctNumber.slice(-4)}</span></> : <>UPI <span className="font-mono">{upi}</span></>}?
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setConfirming(false); }}>Cancel</Button>
            {confirming
              ? <Button data-testid="final-confirm-withdraw-btn" onClick={() => submit()} disabled={busy} className="bg-amber-600 hover:bg-amber-700">{busy ? "Requesting…" : "Yes, withdraw"}</Button>
              : <Button data-testid="confirm-withdraw-btn" onClick={doWithdraw} disabled={busy} className="bg-amber-600 hover:bg-amber-700">{detailsChanged ? "Verify with OTP & withdraw" : "Request withdrawal"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CarrierEarnings;
